import type {
  MapInteractiveZoneActionOptions,
  MapInteractiveZones,
  ZoneLink,
  ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import React from "react";

/** How the viewer shows zones: the legend's category toggles and hover,
 *  heat colours, and linked-zone navigation. Absent in the editor, where
 *  every zone draws as is. */
export interface ZoneViewOptions {
  /** Categories the legend has switched on; category-tagged zones stay
   *  hidden until then. */
  visibleCategories: Set<string>;
  /** Legend hover: zones of this category brighten, the rest fade. */
  categoryHover: string | null;
  /** Linked zone under the pointer, on the map or in the legend. */
  hoveredLinkZoneId: string | null;
  /** Per-zone fill override (campus heatmaps). */
  zoneColors?: Record<string, string>;
  onZoneLinkClick?: (link: ZoneLink) => void;
  onLinkZoneHover?: (zoneId: string | null) => void;
}

/** Map-wide inputs a shape needs besides its own data. Rarely changes. */
export interface RenderOptions {
  zoneTypes: ZoneTypeDefinition[];
  /** Resource id -> team colour, applied to spots without a manual colour. */
  teamColors?: Record<string, string>;
  /** Per-resource state the host supplies (e.g. walk-up), drawn on the dot. */
  interactiveZones?: MapInteractiveZones;
  /** Viewer: spots are click and hover targets for the host. */
  isViewMode?: boolean;
  highlightResource?: (
    resourceId: string | null,
    options: MapInteractiveZoneActionOptions
  ) => void;
  zoneView?: ZoneViewOptions;
}

const EMPTY_OPTIONS: RenderOptions = { zoneTypes: [] };

const RenderOptionsContext = React.createContext<RenderOptions>(EMPTY_OPTIONS);

export const RenderOptionsProvider = RenderOptionsContext.Provider;

export const useRenderOptions = (): RenderOptions =>
  React.useContext(RenderOptionsContext);
